import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Alert,
  Card,
  CardContent,
  OutlinedInput,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  Inventory as InventoryIcon,
  LocalOffer as PriceIcon,
  Receipt as TaxIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  CurrencyExchange as CurrencyIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { productService, mastersService } from '../utils/apiService';
import { useAuth } from '../context/AuthContext';
import { translateToArabic, debounce } from '../utils/translateService';
import settingsService from '../services/settingsService';
import { useTranslation } from '../hooks/useTranslation';
import image1 from "../assets/riyal.png";

const API_URL = process.env.REACT_APP_API_URL || 'https://zatca-simple-invoice-main.onrender.com/api';

const NewItem = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTenant } = useAuth();
  const { t } = useTranslation();
  const isEditMode = Boolean(id);

  const [activeTab, setActiveTab] = useState(0);
  const [autoCodeEnabled, setAutoCodeEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itemGroups, setItemGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [formData, setFormData] = useState({
    productName: '',
    productCode: '',
    alternateName: '',
    barcode: '',
    altBarcode: '',
    group: '',
    itemType: 'goods',
    description: '',
    basicUnit: '',
    alternateUnit: '',
    purchaseRate: '',
    altPurchaseRate: '',
    salesRate: '',
    altSalesRate: '',
    hsnCode: '',
    taxRate: 15,
    taxType: 'standard',
    priceWithTax: '',
    isActive: true
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  // Debounced translation function
  const debouncedTranslate = React.useCallback(
    debounce(async (text) => {
      if (!text || !text.trim()) {
        return;
      }

      setIsTranslating(true);
      try {
        const arabicText = await translateToArabic(text);
        if (arabicText) {
          setFormData(prev => ({
            ...prev,
            alternateName: arabicText
          }));
        }
      } catch (error) {
        console.error('Translation failed:', error);
      } finally {
        setIsTranslating(false);
      }
    }, 800), // Wait 800ms after user stops typing
    []
  );

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // Wait for tenant information to be available
        if (!currentTenant) {
          console.log('Waiting for tenant information...');
          return;
        }

        const tenantId = currentTenant.id || currentTenant._id;
        if (!tenantId) {
          console.error('No tenant ID available');
          setError('Unable to load item groups: No tenant ID available');
          return;
        }

        console.log('Initializing with tenant:', tenantId);

        await fetchSettings();

        // Load data
        if (isEditMode) {
          await fetchProduct();
        }
        await fetchMasters();
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message || 'Failed to initialize component');
      }
    };

    initializeComponent();
  }, [id, currentTenant]);

  const fetchSettings = async () => {
    try {
      const response = await settingsService.getSettings();
      if (response.success && response.data) {
        const isAutoCode = response.data.general?.itemAutoCode;
        setAutoCodeEnabled(isAutoCode);
        if (isAutoCode && !isEditMode) {
          try {
            const autoCodeResponse = await settingsService.getAutoCode('item');
            if (autoCodeResponse.success && autoCodeResponse.data) {
              setFormData(prev => ({
                ...prev,
                productCode: autoCodeResponse.data
              }));
            }
          } catch (codeErr) {
            console.error('Error fetching auto code:', codeErr);
            // Fallback to timestamp based if API fails
            setFormData(prev => ({
              ...prev,
              productCode: `ITEM-${Math.floor(Date.now() / 1000).toString().slice(-6)}`
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchProduct = async () => {
    try {
      const product = await productService.getProduct(id);
      const salesRate = product.salesRate || '';
      const taxRate = product.taxRate || (product.taxType === 'standard' ? 15 : 0);
      const computedPriceWithTax = product.priceWithTax
        ? product.priceWithTax
        : salesRate !== ''
          ? parseFloat((parseFloat(salesRate) * (1 + parseFloat(taxRate) / 100)).toFixed(2))
          : '';
      setFormData({
        productName: product.productName || '',
        productCode: product.productCode || '',
        alternateName: product.alternateName || '',
        barcode: product.barcode || '',
        altBarcode: product.altBarcode || '',
        group: product.groupId || '',
        itemType: product.itemType || 'goods',
        description: product.description || '',
        basicUnit: product.basicUnitId || '',
        alternateUnit: product.alternateUnitId || '',
        purchaseRate: product.purchaseRate || '',
        altPurchaseRate: product.altPurchaseRate || '',
        salesRate: salesRate,
        altSalesRate: product.altSalesRate || '',
        hsnCode: product.hsnCode || '',
        taxRate: taxRate,
        taxType: product.taxType || 'standard',
        priceWithTax: computedPriceWithTax,
        isActive: product.isActive !== undefined ? product.isActive : true
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      setError(error.message || 'Failed to fetch product');
    }
  };

  const fetchMasters = async () => {
    try {
      if (!currentTenant) {
        console.error('No tenant information available');
        setError('Unable to load master data: No tenant information available');
        return;
      }

      const tenantId = currentTenant.id || currentTenant._id;
      if (!tenantId) {
        console.error('No tenant ID available');
        setError('Unable to load master data: No tenant ID available');
        return;
      }

      console.log('Fetching masters for tenant:', tenantId);
      const masters = await mastersService.getAllMasters(tenantId);

      if (masters.itemGroups) {
        console.log('Loaded item groups:', masters.itemGroups);
        setItemGroups(masters.itemGroups);
      }

      if (masters.units) {
        console.log('Loaded units:', masters.units);
        setUnits(masters.units);
      } else {
        console.error('No units found in response');
        setError('Failed to load units');
      }
    } catch (err) {
      console.error('Error fetching masters:', err);
      setError(err.message || 'Failed to fetch master data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Auto-calculate priceWithTax when salesRate changes
      if (name === 'salesRate') {
        const sr = parseFloat(value);
        const tr = parseFloat(updated.taxRate) || 0;
        if (value === '' || value === null) {
          // User cleared the field — clear priceWithTax too
          updated.priceWithTax = '';
        } else if (!isNaN(sr)) {
          const raw = sr * (1 + tr / 100);
          const rounded = Math.round(raw * 100) / 100;
          updated.priceWithTax = rounded;
        }
        // If value is a partial number (e.g. "10.") keep priceWithTax as-is
      }

      // Auto-back-calculate salesRate when priceWithTax changes
      if (name === 'priceWithTax') {
        const pwt = parseFloat(value);
        const tr = parseFloat(updated.taxRate) || 0;
        if (value === '' || value === null) {
          // User cleared the field — clear salesRate too
          updated.salesRate = '';
        } else if (!isNaN(pwt)) {
          const raw = pwt / (1 + tr / 100);
          const rounded = Math.round(raw * 100) / 100;
          updated.salesRate = rounded;
        }
        // If value is partial keep salesRate as-is
      }

      return updated;
    });

    // Auto-translate when product name changes
    if (name === 'productName' && value.trim()) {
      debouncedTranslate(value);
    }
  };

  // Round rate fields to 2 decimal places on blur
  const handleRateBlur = (e) => {
    const { name, value } = e.target;
    const rateFields = ['purchaseRate', 'salesRate', 'priceWithTax'];
    if (rateFields.includes(name)) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        const rounded = Math.round(num * 100) / 100;
        setFormData(prev => ({
          ...prev,
          [name]: rounded
        }));
      } else if (value === '' || value === null) {
        // Leave empty as-is
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.productCode.trim()) {
      errors.productCode = t('Items.Product_code_required');
    }

    if (!formData.productName.trim()) {
      errors.productName = t('Items.Product_name_required');
    }

    if (!formData.alternateName.trim()) {
      errors.alternateName = t('Items.Arabic_name_required');
    }

    if (!formData.group) {
      errors.group = t('Items.Group_required');
    }

    if (!formData.basicUnit) {
      errors.basicUnit = t('Items.Basic_unit_required');
    }

    // Rate validations
    const purchaseRate = parseFloat(formData.purchaseRate);
    const salesRate = parseFloat(formData.salesRate);

    // Basic required validations
    if (!salesRate || salesRate <= 0) {
      errors.salesRate = t('Items.Sales_rate_required');
    }

    // Only validate rate relationships if purchase rate is provided
    if (purchaseRate && salesRate) {
      if (purchaseRate > salesRate) {
        errors.purchaseRate = t('Items.Purchase_rate_validation', { purchaseRate, salesRate });
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await productService.updateProduct(id, formData);
      } else {
        await productService.createProduct(formData);
      }
      navigate('/items');
    } catch (err) {
      setError(err.message || t('Items.Failed_to_save_product'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/items');
  };

  const handleTaxTypeChange = (e) => {
    const { value } = e.target;
    setFormData(prev => {
      const newTaxRate = value === 'standard' ? 15 : 0;
      const sr = parseFloat(prev.salesRate) || 0;
      const newPriceWithTax = sr > 0
        ? parseFloat((sr * (1 + newTaxRate / 100)).toFixed(2))
        : '';
      return {
        ...prev,
        taxType: value,
        taxRate: newTaxRate,
        priceWithTax: newPriceWithTax
      };
    });
  };

  const controlStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 36,
    },
    '& .MuiInputBase-input': {
      py: 0.75,
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem'
    },
    '& .MuiSelect-select': {
      py: 0.75
    }
  };

  const multilineStyle = {
    ...controlStyle,
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      minHeight: 36,
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleCancel}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                {isEditMode ? t('Items.Edit_Item') : t('Items.New_Item')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                {isEditMode ? t('Items.Update_item_information') : t('Items.Add_a_new_item_to_your_inventory')}
              </Typography>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              mb: 0.5,
              borderRadius: '4px',
              boxShadow: 'none',
              border: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 0.5,
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 120,
                  py: 0.5,
                  minHeight: 36,
                  fontSize: '0.875rem'
                }
              }}
            >
              <Tab label={t('Items.Basic_Information')} />
              <Tab label={t('Items.Alternative_UOM')} />
            </Tabs>

            <Grid container spacing={0.5}>
              {/* Basic Information Tab */}
              {activeTab === 0 && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('Items.Product_Code')}
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_product_code')}
                      sx={controlStyle}
                      autoComplete="off"
                      autoFocus={!autoCodeEnabled}
                      disabled={autoCodeEnabled || isEditMode}
                      error={!!validationErrors.productCode}
                      helperText={validationErrors.productCode}
                    />

                    <TextField
                      label={t('Items.Product_Name')}
                      name="productName"
                      value={formData.productName}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_product_name')}
                      sx={controlStyle}
                      autoComplete="off"
                    />

                    <TextField
                      label={t('Items.Arabic_Name')}
                      name="alternateName"
                      value={formData.alternateName}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_arabic_name')}
                      sx={controlStyle}
                      autoComplete="off"
                      error={!!validationErrors.alternateName}
                      helperText={validationErrors.alternateName || (isTranslating ? t('Items.Translating') : '')}
                      InputProps={{
                        endAdornment: isTranslating ? (
                          <InputAdornment position="end">
                            <Chip
                              label={t('Items.Translating')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </InputAdornment>
                        ) : null
                      }}
                    />

                    <TextField
                      label={t('Items.Barcode')}
                      name="barcode"
                      value={formData.barcode}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_barcode')}
                      sx={controlStyle}
                      autoComplete="off"
                    />

                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>{t('Items.Item_Type')}</InputLabel>
                      <Select
                        name="itemType"
                        value={formData.itemType}
                        onChange={handleChange}
                        label={t('Items.Item_Type')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="goods" sx={{ fontSize: '0.875rem' }}>{t('Items.Goods')}</MenuItem>
                        <MenuItem value="service" sx={{ fontSize: '0.875rem' }}>{t('Items.Service')}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth margin="dense" size="small" required error={!!validationErrors.group}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>{t('Items.Group')}</InputLabel>
                      <Select
                        name="group"
                        value={formData.group || ''}
                        onChange={handleChange}
                        label={t('Items.Group')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                          <em>{t('Items.Select_a_group')}</em>
                        </MenuItem>
                        {itemGroups.map((group) => (
                          <MenuItem key={group._id} value={group._id} sx={{ fontSize: '0.875rem' }}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.group && (
                        <FormHelperText error>{validationErrors.group}</FormHelperText>
                      )}
                    </FormControl>

                    <TextField
                      label={t('Items.Description')}
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={4}
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_item_description')}
                      sx={multilineStyle}
                      autoComplete="off"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={handleChange}
                          name="isActive"
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4361ee',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4361ee',
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {formData.isActive ? (
                            <>
                              <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: '1rem' }} />
                              <Typography sx={{ fontSize: '0.875rem' }}>{t('Common.Active')}</Typography>
                            </>
                          ) : (
                            <>
                              <CancelIcon sx={{ color: 'error.main', mr: 1, fontSize: '1rem' }} />
                              <Typography sx={{ fontSize: '0.875rem' }}>{t('Common.Inactive')}</Typography>
                            </>
                          )}
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="dense" size="small" required error={!!validationErrors.basicUnit}>
                      <InputLabel sx={{ fontSize: '0.875rem' }}>{t('Items.Basic_Unit')}</InputLabel>
                      <Select
                        name="basicUnit"
                        value={formData.basicUnit}
                        onChange={handleChange}
                        label={t('Items.Basic_Unit')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                          <em>{t('Items.Select_a_unit')}</em>
                        </MenuItem>
                        {units.map((unit) => (
                          <MenuItem key={unit._id} value={unit._id} sx={{ fontSize: '0.875rem' }}>
                            {unit.name} ({unit.symbol})
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.basicUnit && (
                        <FormHelperText error>{validationErrors.basicUnit}</FormHelperText>
                      )}
                    </FormControl>

                    <TextField
                      label={t('Items.Purchase_Rate')}
                      name="purchaseRate"
                      value={formData.purchaseRate}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder={t('Items.Enter_purchase_rate')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={image1} alt="currency" style={{ width: '16px', height: '16px' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                      onBlur={handleRateBlur}
                      error={!!validationErrors.purchaseRate}
                      helperText={validationErrors.purchaseRate}
                    />

                    <TextField
                      label={t('Items.Sales_Rate_Excluding_Tax')}
                      name="salesRate"
                      value={formData.salesRate}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder={t('Items.Enter_sales_rate')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"><img src={image1} alt="currency" style={{ width: '16px', height: '16px' }} /></InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                      onBlur={handleRateBlur}
                      error={!!validationErrors.salesRate}
                      helperText={validationErrors.salesRate}
                    />

                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>{t('Items.Tax_Type')}</InputLabel>
                      <Select
                        name="taxType"
                        value={formData.taxType}
                        onChange={handleTaxTypeChange}
                        label={t('Items.Tax_Type')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="standard" sx={{ fontSize: '0.875rem' }}>{t('Items.Standard')}</MenuItem>
                        <MenuItem value="exempt" sx={{ fontSize: '0.875rem' }}>{t('Items.Exempt')}</MenuItem>
                        <MenuItem value="zero" sx={{ fontSize: '0.875rem' }}>{t('Items.Zero_Rated')}</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label={t('Items.Rate_of_Tax')}
                      name="taxRate"
                      value={formData.taxRate}
                      fullWidth
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_tax_rate')}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                    />

                    <TextField
                      label={t('Items.Sales_Rate_Including_Tax')}
                      name="priceWithTax"
                      value={formData.priceWithTax}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder={t('Items.Enter_sales_rate_incl_tax')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={image1} alt="currency" style={{ width: '16px', height: '16px' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                      onBlur={handleRateBlur}
                    />
                  </Grid>
                </>
              )}

              {/* Alternative UOM Tab */}
              {
                activeTab === 1 && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('Items.Alternate_Barcode')}
                      name="altBarcode"
                      value={formData.altBarcode}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      placeholder={t('Items.Enter_alternate_barcode')}
                      sx={controlStyle}
                      autoComplete="off"
                    />

                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel sx={{ fontSize: '0.875rem' }}>{t('Items.Alternate_Unit')}</InputLabel>
                      <Select
                        name="alternateUnit"
                        value={formData.alternateUnit}
                        onChange={handleChange}
                        label={t('Items.Alternate_Unit')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            height: 36,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0,
                          },
                          '& .MuiSelect-select': {
                            py: 0.75,
                            fontSize: '0.875rem',
                            borderRadius: 0,
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.875rem'
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                          <em>{t('Items.None')}</em>
                        </MenuItem>
                        {units.map((unit) => (
                          <MenuItem key={unit._id} value={unit._id} sx={{ fontSize: '0.875rem' }}>
                            {unit.name} ({unit.symbol})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label={t('Items.Alternate_Purchase_Rate')}
                      name="altPurchaseRate"
                      value={formData.altPurchaseRate}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder={t('Items.Enter_alternate_purchase_rate')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={image1} alt="currency" style={{ width: '15px', height: '15px' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                    />

                    <TextField
                      label={t('Items.Alternate_Sales_Rate')}
                      name="altSalesRate"
                      value={formData.altSalesRate}
                      onChange={handleChange}
                      fullWidth
                      margin="dense"
                      size="small"
                      type="number"
                      placeholder={t('Items.Enter_alternate_sales_rate')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={image1} alt="currency" style={{ width: '15px', height: '15px' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={controlStyle}
                      autoComplete="off"
                    />
                  </Grid>
                )
              }
            </Grid >
          </Paper >

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              disabled={loading}
            >
              {t('Common.Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? t('Items.Saving') : isEditMode ? t('Common.Update') : t('Common.Save')}
            </Button>
          </Box>
        </form >
      </Paper >
    </Container >
  );
};

export default NewItem; 